import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { EMPTY_MANAGED_FILTERS, managedFiltersQuery, managedTableQuery, unassignedTallyQuery, type ManagedFilters } from '../api/inventoree'
import type { Named } from '../api/shell'
import { DeviceWorkspace } from '../devices/DeviceWorkspace'
import { DataTable, useTableState } from '../ui/DataTable'
import { Forbidden, isForbidden } from '../ui/Forbidden'
import { Content } from '../ui/Shell'
import { useManagedRowActions } from './ManagedRowActions'
import { managedColumns, type ManagedRow } from './managedColumns'
import { MultiActionsSidebar } from './MultiActionsSidebar'
import { useUnassignedBand } from './UnassignedBand'
import { UnassignedAssignBar, UnassignedCount, useAssignCell } from './UnassignedFirst'

/**
 * Inventoree::live_agentbased - #overviewDevices, OverviewDevicesModel,
 * filtrele din overview/filters/devicesDatatable.php.
 */

function Select({ label, name, options, value, onChange }: { label: string; name: string; options: Named[]; value: string; onChange(v: string): void }) {
  if (options.length === 0) return null
  return (
    <div className="col-md-4">
      <label>{label}</label>
      <select className="form-control input-sm" name={name} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Select...</option>
        {options.map((o) => (
          <option key={o.id} value={String(o.id)}>
            {o.name}
          </option>
        ))}
      </select>
    </div>
  )
}

function Switchery({ checked, onChange }: { checked: boolean; onChange(v: boolean): void }) {
  return (
    <>
      <input type="checkbox" className="switchery" name="inactive" value="1" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ display: 'none' }} />
      <span
        className="switchery switchery-small"
        role="switch"
        aria-checked={checked}
        tabIndex={0}
        onClick={() => onChange(!checked)}
        onKeyDown={(e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault()
            onChange(!checked)
          }
        }}
        style={{
          backgroundColor: checked ? 'rgb(0, 144, 217)' : 'rgb(255, 255, 255)',
          borderColor: checked ? 'rgb(0, 144, 217)' : 'rgb(221, 221, 221)',
          boxShadow: checked ? 'rgb(0, 144, 217) 0px 0px 0px 16px inset' : 'rgb(221, 221, 221) 0px 0px 0px 0px inset',
          transition: 'border 0.4s, box-shadow 0.4s, background-color 1.2s',
        }}
      >
        <small style={{ left: checked ? 13 : 0, transition: 'background-color 0.4s, left 0.2s' }}></small>
      </span>
    </>
  )
}

function FiltersPanel({ applied, onApply, close }: { applied: ManagedFilters; onApply(f: ManagedFilters): void; close(): void }) {
  const options = useQuery(managedFiltersQuery)
  const [draft, setDraft] = useState<ManagedFilters>(applied)
  const o = options.data
  const set = (key: keyof ManagedFilters) => (v: string) => setDraft((d) => ({ ...d, [key]: v }))

  return (
    <>
      <header className="panel-heading sm bg-default">
        <h4>
          <i className="fa fa-filter"></i> <strong>Apply</strong> filters
        </h4>
      </header>
      <div className="panel-body">
        <form
          className="form-horizontal labelcustomize tableFilters"
          data-collabel="3"
          data-alignlabel="left"
          data-label="color"
          method="post"
          action=""
          onSubmit={(e) => {
            e.preventDefault()
            onApply(draft)
          }}
          onReset={(e) => {
            e.preventDefault()
            setDraft(EMPTY_MANAGED_FILTERS)
          }}
        >
          <div className="form-group">
            <div className="col-md-4">
              <label>Type</label>
              <select className="form-control input-sm" name="deviceType" value={draft.deviceType} onChange={(e) => set('deviceType')(e.target.value)}>
                <option value="">All types</option>
                <option value="workstations">Workstations</option>
                <option value="servers">Servers</option>
                <option value="mobiles">Mobiles</option>
              </select>
            </div>
            <Select label="Office" name="office" options={o?.offices ?? []} value={draft.office} onChange={set('office')} />
            <Select label="Department" name="department" options={o?.departments ?? []} value={draft.department} onChange={set('department')} />
          </div>
          <div className="form-group">
            <Select label="Group" name="group" options={o?.groups ?? []} value={draft.group} onChange={set('group')} />
            <Select label="Owner" name="owner" options={o?.owners ?? []} value={draft.owner} onChange={set('owner')} />
            <div className="col-md-4">
              <label className="col-sm-12">Inactive devices</label>
              <div className="col-sm-12">
                <Switchery checked={draft.inactive} onChange={(v) => setDraft((d) => ({ ...d, inactive: v }))} />
              </div>
            </div>
          </div>
          <div className="form-group offset">
            <div className="col-md-offset-5 col-md-7">
              <button type="submit" className="btn btn-theme applyFilters">
                Apply
              </button>{' '}
              <button type="reset" className="btn btn-inverse">
                Reset
              </button>{' '}
              <button type="button" className="btn closeFilters" onClick={close}>
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  )
}

export function ManagedPage() {
  const [state, setState] = useTableState('overviewDevices', { start: 0, length: 25, order: { column: 3, dir: 'desc' }, search: '' })
  const [filters, setFilters] = useState<ManagedFilters>(EMPTY_MANAGED_FILTERS)
  const [selected, setSelected] = useState<Set<number>>(() => new Set())
  /* Cate aparate stau nealocate, si ce are omul voie cu ele. Se cere INAINTEA
     coloanelor: de `canAssign` atarna ce scrie celula de proprietar. */
  const tally = useQuery(unassignedTallyQuery)
  const columns = useMemo(() => managedColumns(undefined, tally.data?.canAssign === true), [tally.data?.canAssign])
  const orderColumn = columns.find((c) => c.index === state.order.column) ?? columns[3]
  const query = useQuery({
    ...managedTableQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: orderColumn.index,
      orderName: orderColumn.name ?? '',
      orderDir: state.order.dir,
      search: state.search,
      filters,
    }),
    placeholderData: keepPreviousData,
  })
  /* Banda rosie din capul tabelului si insigna rosie din antet: statiile
     fara utilizator din scopul listei (UnassignedBand). */
  const unknown = useUnassignedBand(query.data?.unassigned, tally.data, () => void query.refetch())
  /* Sertarul "Action" al fiecarui rand: wake / restart / uninstall / check / notify. */
  const rowActions = useManagedRowActions(() => void query.refetch())
  /* Butonul Assign din CELULA de proprietar, in locul textului "Unassigned":
     acelasi drept si aceeasi fereastra ca banda din randul desfacut. */
  const assignCell = useAssignCell(tally.data, () => void query.refetch())

  if (isForbidden(query.error)) return <Forbidden />

  const rows = (query.data?.data ?? []) as unknown as ManagedRow[]
  const activeFilters = Object.entries(filters).filter(([k, v]) => (k === 'inactive' ? v === true : v !== '')).length

  const onTableClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (assignCell.onAssignCellClick(e)) return
    rowActions.onClick(e)
    const target = e.target as HTMLElement
    if (target instanceof HTMLInputElement && target.classList.contains('oneDevice')) {
      const idNum = Number(target.value)
      setSelected((s) => {
        const next = new Set(s)
        if (target.checked) next.add(idNum)
        else next.delete(idNum)
        return next
      })
    } else if (target instanceof HTMLInputElement && target.classList.contains('allDevices')) {
      setSelected(target.checked ? new Set(rows.map((r) => r.ID)) : new Set())
    }
  }

  return (
    <Content>
      {rowActions.modal}
      {assignCell.modal}
      <div onClick={onTableClick}>
        <DataTable
          id="overviewDevices"
          columns={columns}
          rows={rows.map((r) => ({ ...r, __checked: selected.has(r.ID) }))}
          total={query.data?.recordsTotal ?? 0}
          fetching={query.isFetching}
          error={query.isError ? (query.error as Error).message : null}
          onRetry={() => void query.refetch()}
          state={state}
          onState={setState}
          rowId={(r) => String(r.DT_RowId)}
          // `DataTablesProperties.buttons` (general.js), pe care portalul le pune pe
          // fiecare lista de aparate: Copy, Columns si Refresh (`dt.ajax.reload(null,
          // false)`) stau in subsol, dupa paginare.
          footerButtons={{ copy: true, colvis: true, refresh: () => void query.refetch() }}
          columnTools
          children={unknown.band}
          headerButtons={
            /* "N unassigned" duce la lista Unknown. Lipseste cand nu e niciunul
               sau cand omul n-are drept acolo. */
            unknown.badge ?? <UnassignedCount tally={tally.data} />
          }
          details={(row) => (
            <>
              {/* Aparatul n-are stapan: banda cu Assign, deasupra fisei. */}
              <UnassignedAssignBar id={Number(row.ID)} row={row} tally={tally.data} onAssigned={() => void query.refetch()} />
              <DeviceWorkspace id={Number(row.ID)} from="inventoree" />
            </>
          )}
          filters={{
            active: activeFilters,
            render: (close) => (
              <FiltersPanel
                applied={filters}
                close={close}
                onApply={(f) => {
                  setFilters(f)
                  setState({ start: 0 })
                }}
              />
            ),
          }}
        />
      </div>
      {/* Zona din dreapta, cat timp e bifat cel putin un rand. */}
      <MultiActionsSidebar ids={[...selected].sort((a, b) => a - b)} />
    </Content>
  )
}
